import React, { FC } from 'react'
import { FlexRow } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { Modal } from '@/molecules/Modal'
import { formatPrice } from '@/services/PriceFormatter'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'
import { BonusGiftType } from '@/views/PreSales/hooks/useBonusGift'

interface ShareProps {
  isOpen: boolean
  onClose: () => void
  bonusGift: BonusGiftType
  onCtaClick: () => void
  theatricalSlug: string
}

export const BonusGiftModal: FC<ShareProps> = ({ isOpen, onClose, bonusGift, onCtaClick, theatricalSlug }) => {
  const { t } = useTranslate('tickets')
  const { imageUrl, valueInPennies } = bonusGift
  const prizeValue = formatPrice(valueInPennies, { currency: 'USD', includeDecimals: false })
  const { presale } = useGiveawaySchedule(theatricalSlug)
  const multiplier = presale?.multiplier ?? 1

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="px-4"
      panelClasses="!w-full !bg-homestead-tan-200 !px-4 !pb-6 !pt-8 md:!p-10 !max-w-[369px] md:!max-w-[600px] !overflow-visible"
    >
      <div className="absolute inset-0 bg-[url(https://images.angelstudios.com/image/upload/v1729801080/dirt-texture-on-white-01.webp)] bg-cover bg-no-repeat opacity-[.16] mix-blend-multiply" />
      <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-black/10 p-2" onClick={onClose}>
        <CloseIcon color="black" size={16} />
      </div>
      <div className="relative z-10 grid w-full justify-items-center text-center">
        <div className="grid w-full gap-1 text-center">
          <p className="mx-auto max-w-[27ch] !font-[dharma-gothic-e] text-[1.75rem] font-bold uppercase leading-[1.75rem] tracking-[0.5px] text-homestead-primary-blue md:text-[2.25rem] md:leading-[2.25rem]">
            <Translate i18nKey="enterYourChanceToWinAV4" t={t}>
              Watch Homestead in <br className="md:hidden" /> Theaters and you Could Win a
            </Translate>
          </p>
          <h2 className="!font-[dharma-gothic-e] text-[3rem] font-black uppercase leading-[40px] tracking-[1.25px] text-homestead-red-dark md:text-[4.5rem] md:leading-[56px] md:tracking-[1.25px]">
            {t('threeThousandHomesteadV2', '$300k Homestead!')}
          </h2>
          <Image
            className="mx-auto md:!h-[27px] md:!w-[32px]"
            src="https://images.angelstudios.com/image/upload/v1732639114/plus-icon-red.png"
            alt=""
            priority
            width={25}
            height={21}
          />

          <FlexRow className="mx-auto gap-3">
            <p className="!font-[dharma-gothic-e] text-[1.875rem] font-black uppercase leading-[40px] tracking-[1.25px] text-homestead-red-dark md:text-[3.5rem] md:leading-[56px]">
              {t('bonusPrize', 'BONUS PRIZE')}
            </p>
            <Image
              alt=""
              src="https://images.angelstudios.com/image/upload/v1732639476/and-slash-letters-red.png"
              priority
              width={30}
              height={16}
              className="md:!h-[22px] md:!w-[43px]"
            />
            <p className="!font-[dharma-gothic-e] text-[1.875rem] font-black uppercase leading-[40px] tracking-[1.25px] text-homestead-red-dark md:text-[3.5rem] md:leading-[56px]">
              {multiplier}
              {t('xentries', 'X ENTRIES')}
            </p>
          </FlexRow>

          <p className="!font-[bio-sans] text-[28px] font-semibold uppercase leading-[20px] text-homestead-primary-blue md:text-[32px] md:leading-[24px]">
            <Translate i18nKey="bonusPrizeFinalWeeked" t={t}>
              <span className="font-black">Final Weekend!</span>
            </Translate>
          </p>

          <p className="!font-[bio-sans] text-[14px] leading-[21px] text-homestead-primary-blue">
            {t('bonusPrizeValuedAt', 'Bonus prize valued at {{prizeValue}}.', { prizeValue })}
          </p>
        </div>

        <Image
          className="mt-3 md:mt-[20px] md:!h-[255px] md:!w-[483px]"
          priority
          src={imageUrl}
          alt="bonus gift"
          width={329}
          height={173}
        />

        <button
          onClick={onCtaClick}
          className="homestead-btn-sm mt-4 rounded-xl bg-homestead-red px-6 py-3.5 uppercase text-white md:mt-[14px]"
        >
          {t('bonusGiftPreOrder', 'Pre-order tickets now')}
        </button>
      </div>
    </Modal>
  )
}
